import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Copy, AlertTriangle } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

function mondayOf(dateStr) {
  const d = dateStr ? new Date(dateStr) : new Date();
  const day = d.getDay();
  d.setDate(d.getDate() + (day === 0 ? -6 : 1 - day));
  return d.toISOString().slice(0, 10);
}

export default function DuplicateWeekDialog({ onDone, groups = [] }) {
  const [open, setOpen] = useState(false);
  const today = new Date().toISOString().slice(0, 10);
  const [fromStart, setFromStart] = useState(() => {
    const d = new Date(); d.setDate(d.getDate() - 7); return mondayOf(d.toISOString().slice(0,10));
  });
  const [toStart, setToStart] = useState(() => mondayOf(today));
  const [groupId, setGroupId] = useState("");
  const [loading, setLoading] = useState(false);
  const [conflicts, setConflicts] = useState(null); // {target_existing, existing_titles, source_count}
  const [mode, setMode] = useState("add"); // add | replace

  const reset = () => { setConflicts(null); setMode("add"); };

  const checkAndRun = async () => {
    setLoading(true);
    try {
      const preview = await api.post("/trainings/duplicate-week", {
        from_start: fromStart, to_start: toStart, group_id: groupId || null, preview: true,
      });
      if (preview.data.source_count === 0) {
        toast.error("Nessun allenamento nella settimana sorgente");
        setLoading(false); return;
      }
      if (preview.data.target_existing > 0 && !conflicts) {
        setConflicts(preview.data);
        setLoading(false); return;
      }
      const { data } = await api.post("/trainings/duplicate-week", {
        from_start: fromStart, to_start: toStart, group_id: groupId || null,
        replace: mode === "replace",
      });
      const parts = [`${data.copied} allenamenti duplicati`];
      if (data.target_replaced) parts.push(`(${data.target_replaced} sostituiti)`);
      toast.success(parts.join(" "));
      setOpen(false); reset(); onDone?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
    finally { setLoading(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) reset(); }}>
      <div onClick={() => setOpen(true)}>
        <Button data-testid="duplicate-week-btn" variant="outline" className="h-12"><Copy className="w-4 h-4 mr-2" />Duplica settimana</Button>
      </div>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Duplica una settimana di allenamenti</DialogTitle>
          <DialogDescription>Copia gli allenamenti da una settimana sorgente in una nuova.</DialogDescription>
        </DialogHeader>

        {!conflicts && (
          <div className="space-y-3 py-2">
            <div>
              <Label>Settimana sorgente (Lun)</Label>
              <Input data-testid="dup-from" type="date" value={fromStart} onChange={(e) => setFromStart(mondayOf(e.target.value))} />
              <div className="text-xs text-zinc-500 mt-1 font-mono">Copia da {fromStart} a Domenica</div>
            </div>
            <div>
              <Label>Settimana destinazione (Lun)</Label>
              <Input data-testid="dup-to" type="date" value={toStart} onChange={(e) => setToStart(mondayOf(e.target.value))} />
              <div className="text-xs text-zinc-500 mt-1 font-mono">Shift automatico di {Math.round((new Date(toStart) - new Date(fromStart)) / (86400000))} giorni</div>
            </div>
            <div>
              <Label>Filtra per gruppo (opzionale)</Label>
              <Select value={groupId || "all"} onValueChange={(v) => setGroupId(v === "all" ? "" : v)}>
                <SelectTrigger><SelectValue placeholder="Tutti" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tutti i gruppi</SelectItem>
                  {groups.map((g) => <SelectItem key={g.id} value={g.id}>{g.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
        )}

        {conflicts && (
          <div className="space-y-4 py-2">
            <div className="bg-amber-50 border border-amber-200 rounded-md p-4 flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5" />
              <div className="text-sm">
                <div className="font-semibold text-amber-900">Attenzione: conflitto di date</div>
                <div className="text-amber-800 mt-1">La settimana di destinazione contiene già <strong className="font-mono">{conflicts.target_existing}</strong> allenamento/i{groupId ? " per questo gruppo" : ""}.</div>
              </div>
            </div>
            <div className="max-h-40 overflow-y-auto no-scrollbar bg-zinc-50 border border-zinc-200 rounded p-2 space-y-1">
              {conflicts.existing_titles.map((e) => (
                <div key={e.id} className="text-xs font-mono flex items-center gap-2">
                  <span className="text-zinc-500">{e.date}</span>
                  <span>{e.title}</span>
                </div>
              ))}
            </div>
            <div>
              <Label>Come procedere?</Label>
              <div className="grid grid-cols-2 gap-2 mt-2">
                <button data-testid="mode-add" onClick={() => setMode("add")} className={`p-3 rounded-md border text-sm text-left ${mode === "add" ? "border-black bg-zinc-50" : "border-zinc-200"}`}>
                  <div className="font-semibold">Aggiungi</div>
                  <div className="text-xs text-zinc-500 mt-1">Mantieni esistenti, aggiungi i {conflicts.source_count} nuovi</div>
                </button>
                <button data-testid="mode-replace" onClick={() => setMode("replace")} className={`p-3 rounded-md border text-sm text-left ${mode === "replace" ? "border-red-600 bg-red-50" : "border-zinc-200"}`}>
                  <div className="font-semibold text-red-700">Sostituisci</div>
                  <div className="text-xs text-zinc-500 mt-1">Elimina i {conflicts.target_existing} esistenti e copia i nuovi</div>
                </button>
              </div>
            </div>
          </div>
        )}

        <DialogFooter>
          {!conflicts ? (
            <>
              <Button variant="outline" onClick={() => setOpen(false)}>Annulla</Button>
              <Button data-testid="dup-run" onClick={checkAndRun} disabled={loading} className="bg-black hover:bg-zinc-800">{loading ? "Verifica…" : "Duplica"}</Button>
            </>
          ) : (
            <>
              <Button variant="outline" onClick={() => setConflicts(null)}>Indietro</Button>
              <Button data-testid="dup-confirm" onClick={checkAndRun} disabled={loading} className={mode === "replace" ? "bg-red-600 hover:bg-red-700" : "bg-black hover:bg-zinc-800"}>
                {loading ? "Copia…" : (mode === "replace" ? "Sostituisci e copia" : "Aggiungi copie")}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
